import { useAuth } from '../context/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import EnquiriesHomeBadge from '../components/EnquiriesHomeBadge';
import TasksHomeBadge from '../components/TasksHomeBadge';

// Same 7-role staff list the /ops-board and /enquiries routes guard on (security/student-read-lockdown).
const STAFF_ROLES = ['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager'];

export default function Dashboard() {
  const { isInitializing, user } = useAuth();

  // The "/" home is the board greeting + board link — a staff surface. Non-staff (student/client)
  // have nothing here, so send them to their profile. Render nothing while auth is initialising
  // (same convention as RequireRole) so we never flash-redirect before roles are known.
  if (isInitializing) return null;
  if (user && !STAFF_ROLES.some((r) => user.roles?.includes(r))) return <Navigate to="/profile" replace />;

  // Header nav (Profile / Admin / Sign out) now lives in the global ☰ menu (AppHeader),
  // so this page's top bar is brand-only.
  return (
    <div style={styles.page}>
      <AppHeader />

      <div style={styles.body}>
        <h2 style={styles.greeting}>
          Welcome back, {user?.preferred_name ?? user?.full_name ?? 'pilot'}
        </h2>
        <p style={styles.sub}>
          {user?.pilot_code && <span style={styles.badge}>{user.pilot_code}</span>}
          {user?.roles?.join(' · ')}
        </p>
        <Link to="/ops-board" style={styles.opsBoardLink}>
          ✈ Open Flying School Ops Board →
        </Link>
        {/* Staff-only open/assigned enquiries alert (self-gates on the /enquiries allow-list). */}
        <EnquiriesHomeBadge />
        {/* Staff-only open/assigned office-tasks alert (self-gates on the /tasks allow-list). */}
        <TasksHomeBadge />
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0f172a',
    color: '#f1f5f9',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  logoutBtn: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: 6,
    color: '#94a3b8',
    fontSize: 13,
    cursor: 'pointer',
  },
  profileLink: {
    padding: '6px 14px',
    border: '1px solid #334155',
    borderRadius: 6,
    color: '#94a3b8',
    fontSize: 13,
    textDecoration: 'none',
  },
  body: {
    padding: '48px 24px',
    maxWidth: 640,
    margin: '0 auto',
  },
  greeting: {
    margin: '0 0 8px',
    fontSize: 24,
    fontWeight: 600,
    color: '#f1f5f9',
  },
  sub: {
    margin: '0 0 32px',
    fontSize: 14,
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  badge: {
    padding: '2px 8px',
    background: '#1d4ed8',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    color: '#bfdbfe',
    letterSpacing: 0.5,
  },
  opsBoardLink: {
    display: 'block',
    padding: '20px 24px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    color: '#7dd3fc',
    textAlign: 'center' as const,
    textDecoration: 'none',
  },
} as const;
